/**
 * School years dashboard section: list with create/edit slide panel.
 */

import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SchoolYearsDataTable } from '@/components/school-years/SchoolYearsDataTable';
import { SlidePanel } from '@/components/common/SlidePanel';
import { CreateSchoolYearView } from '@/components/school-years/CreateSchoolYearView';
import { EditSchoolYearView } from '@/components/school-years/EditSchoolYearView';
import { usePageActions } from '@/hooks/usePageActions';
import type { ElementWidth } from '@/types/interface-elements.types';

/** Props for {@link SchoolYearsSection}. */
interface SchoolYearsSectionProps {
  onSuccess?: () => void;
  layoutWidth?: ElementWidth;
}

/** Dashboard widget: school years list. */
export const SchoolYearsSection: React.FC<SchoolYearsSectionProps> = ({
  onSuccess,
  layoutWidth = 'full',
}) => {
  const queryClient = useQueryClient();
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions('schoolYears');
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const [editSchoolYearId, setEditSchoolYearId] = React.useState<string | null>(null);

  const handleCreateSchoolYear = () => {
    setCreateFormOpen(true);
    setEditSchoolYearId(null);
  };

  const handleEditSchoolYear = (id: string) => {
    setEditSchoolYearId(id);
    setCreateFormOpen(false);
  };

  return (
    <>
      <SchoolYearsDataTable
        layoutWidth={layoutWidth}
        isSection={true}
        onEditSchoolYear={handleEditSchoolYear}
        showCreateButton={canCreate}
        onCreateSchoolYear={handleCreateSchoolYear}
        pageActions={{ canEditRow, canDeleteRow, canExportCsv }}
      />

      <SlidePanel isOpen={createFormOpen} onClose={() => setCreateFormOpen(false)}>
        <CreateSchoolYearView
          onClose={() => setCreateFormOpen(false)}
          onSuccess={() => {
            setCreateFormOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['schoolYears'] });
            void queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
            void queryClient.invalidateQueries({ queryKey: ['dashboardCharts'] });
            onSuccess?.();
          }}
        />
      </SlidePanel>

      <SlidePanel isOpen={editSchoolYearId !== null} onClose={() => setEditSchoolYearId(null)}>
        {editSchoolYearId && (
          <EditSchoolYearView
            key={editSchoolYearId}
            schoolYearId={editSchoolYearId}
            onClose={() => setEditSchoolYearId(null)}
            onSuccess={() => {
              setEditSchoolYearId(null);
              void queryClient.invalidateQueries({ queryKey: ['schoolYears'] });
              void queryClient.invalidateQueries({ queryKey: ['dashboardStats'] });
              void queryClient.invalidateQueries({ queryKey: ['dashboardCharts'] });
              onSuccess?.();
            }}
          />
        )}
      </SlidePanel>
    </>
  );
};
